/**
 * RFC 4180, read.
 *
 * **Extracted from `routes/reporting/items-export.ts`, where it was private and
 * where it was the only copy.** FE208 needs to read a file a person hands the
 * app rather than one the API wrote, and `docs/ui-conventions.md` is blunt about
 * what happens next: "a rule used by two forms lives in one place… two copies
 * drift and nothing catches it". A second CSV reader written for the upload
 * would have been a second opinion about what a quoted comma means, and the two
 * would have disagreed the first time somebody exported a catalogue, edited it
 * and uploaded it back.
 *
 * Splitting on commas and newlines is wrong on the first item with a comma in
 * its name — every column after it shifts by one, and a price is read out of a
 * name without anything failing. So the quoting is read properly: a doubled `"`
 * inside a quoted field is one quote of data, and a field may carry commas and
 * newlines while it is quoted.
 *
 * A hand-written reader rather than a dependency. This is the whole of what is
 * needed — one shape in, one shape out — and a CSV library is a package to keep
 * current for it.
 *
 * Bare (unquoted) fields are read as well as quoted ones. The API's writer
 * quotes every cell unconditionally and terminates every line with CRLF, but a
 * file that has been through a spreadsheet has neither of those properties, and
 * a spreadsheet is exactly what the catalogue upload is fed from.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];

  let cells: string[] = [];
  let cell = '';
  let quoted = false;
  // Whether anything at all has been seen since the last row terminator. It is
  // what tells a trailing CRLF — every file ends with one — from a genuine empty
  // final row, which would otherwise be appended to every file this reads.
  let started = false;

  const endCell = () => {
    cells.push(cell);
    cell = '';
  };

  const endRow = () => {
    endCell();
    rows.push(cells);
    cells = [];
    started = false;
  };

  for (let at = 0; at < text.length; at += 1) {
    const character = text[at];

    started = true;

    if (quoted) {
      if (character !== '"') {
        cell += character;
      } else if (text[at + 1] === '"') {
        // A doubled quote is one quote of data, and the second must not be read
        // as the closing one.
        cell += '"';
        at += 1;
      } else {
        quoted = false;
      }

      continue;
    }

    if (character === '"') {
      quoted = true;
    } else if (character === ',') {
      endCell();
    } else if (character === '\r' && text[at + 1] === '\n') {
      endRow();
      at += 1;
    } else if (character === '\n') {
      // A lone LF, for a file that has been through a tool that rewrote its
      // terminators. The API never writes one; a spreadsheet on a Mac does.
      endRow();
    } else {
      cell += character;
    }
  }

  if (started) endRow();

  return rows;
}
